import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { HiHome, HiChartBar, HiBriefcase } from 'react-icons/hi2';
import { useWallet } from '../../hooks/useWallet';

/**
 * Desktop left navigation sidebar.
 * Expanded: 160px with labels. Collapsed: 48px icon-only strip.
 * Toggle button persists state in localStorage.
 * Hidden on mobile (<md).
 */
export function WorkspaceSidebar({ className = '', portfolioId }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { account } = useWallet();

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem('sidebar_collapsed') === 'true';
    } catch {
      return false;
    }
  });

  const toggle = () => {
    setCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('sidebar_collapsed', String(next)); } catch {}
      return next;
    });
  };

  const items = [
    { key: 'home', label: 'Home', Icon: HiHome, path: '/' },
    { key: 'dashboard', label: 'Dashboard', Icon: HiChartBar, path: '/dashboard' },
  ];

  return (
    <div
      className={`${collapsed ? 'w-12' : 'w-50'} border-r border-slate-700 bg-slate-900/80 flex flex-col transition-all duration-200 ${className}`}
    >
      {/* Toggle button */}
      <div className="flex items-center justify-end px-2 pt-2 pb-1">
        <button
          onClick={toggle}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="p-1 rounded text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
        >
          <svg
            className={`w-6 h-6 transition-transform duration-200 ${collapsed ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7M18 19l-7-7 7-7" />
          </svg>
        </button>
      </div>

      <nav className="flex-1 px-1.5 pb-3 flex flex-col gap-1">
        {items.map(item => {
          const isActive = location.pathname === item.path;
          return (
            <button
              key={item.key}
              onClick={() => navigate(item.path)}
              title={collapsed ? item.label : undefined}
              className={`flex items-center gap-2 rounded text-sm transition-colors whitespace-nowrap w-full
                ${collapsed ? 'justify-center px-1 py-1.5' : 'px-2 py-1.5 text-left'}
                ${isActive
                  ? 'bg-slate-700 text-white font-semibold'
                  : 'text-slate-300 hover:bg-slate-800'
                }`}
            >
              <item.Icon className="w-6 h-6 text-cyan-400 flex-shrink-0" />
              {!collapsed && <span>{item.label}</span>}
            </button>
          );
        })}

        {/* Current workspace indicator */}
        {portfolioId && (
          <div
            title={collapsed ? 'Workspace' : undefined}
            className={`flex flex-row mt-2 rounded text-xs bg-slate-800 text-cyan-400 font-semibold
              ${collapsed ? 'justify-center px-1 py-1.5' : 'px-2 py-1.5 truncate'}`}
          >
            <HiBriefcase className="w-6 h-6 text-cyan-400 flex-shrink-0" />
            {!collapsed && <span className="ml-1.5 truncate">Workspace</span>}
          </div>
        )}
      </nav>

      {/* Account badge */}
      <div className="px-2 py-3 border-t border-slate-700">
        {collapsed ? (
          <div
            title={account || ''}
            className="w-7 h-7 rounded-full bg-slate-700 flex items-center justify-center text-xs text-slate-300 font-mono mx-auto"
          >
            {account ? account.slice(2, 4).toUpperCase() : '?'}
          </div>
        ) : (
          <div className="text-xs text-slate-400 font-mono truncate">
            {account ? `${account.slice(0, 6)}...${account.slice(-4)}` : ''}
          </div>
        )}
      </div>
    </div>
  );
}
